/* eslint-disable camelcase */
import React, { useState, useEffect } from "react"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Divider,
  Badge,
  Chip,
  Avatar,
  Alert,
} from "@mui/material"
import { CloudOff, CloudDone, Sync, SwapHoriz, PersonOff, Save, Warning, Public } from "@mui/icons-material"
import localforage from "localforage"
import Swal from "sweetalert2"

import Header from "../../../../layouts/Header"
import BackIcon from "../../../../components/BackIcon"
import { api } from "../../../../api"

// Tema SIAC Táctico
const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#ed6c02" },
    success: { main: "#2E7D32" },
    error: { main: "#d32f2f" },
  },
})

const ReasignacionMesas = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [isLoading, setIsLoading] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)

  // Estados de datos y seguridad RLS
  const [juntas, setJuntas] = useState([])
  const [delegadosActivos, setDelegadosActivos] = useState([])
  const [colaReasignacion, setColaReasignacion] = useState([])
  const [errorAcceso, setErrorAcceso] = useState(null)
  const [recintoActual, setRecintoActual] = useState("")
  const [esGlobal, setEsGlobal] = useState(false)

  // Estado temporal para los selects de cambio
  const [cambiosPendientes, setCambiosPendientes] = useState({})

  // ========================================================
  // 1. INICIALIZACIÓN Y CARGA DE DATOS
  // ========================================================
  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    cargarDatosLogisticos()
    verificarColaLocal()

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  const verificarColaLocal = async () => {
    const cola = (await localforage.getItem("reasignacion_offline_queue")) || []
    setColaReasignacion(cola)
  }

  const cargarDatosLogisticos = async () => {
    setIsLoading(true)
    setErrorAcceso(null)
    try {
      if (isOffline) throw new Error("Offline")

      const res = await api.post("/ControlElectoral/getMesasYDelegados", {})

      if (res.data && res.data.success === false) {
        setErrorAcceso(res.data.message)
        return
      }

      const realesJuntas = res.data.data.juntas || []
      const realesDelegados = res.data.data.delegadosActivos || []
      const recintoAsignado = res.data.recinto || ""
      const isGlobalBackend = res.data.es_global || false

      setJuntas(realesJuntas)
      setDelegadosActivos(realesDelegados)
      setRecintoActual(recintoAsignado)
      setEsGlobal(isGlobalBackend)

      await localforage.setItem("coord_juntas_cache", realesJuntas)
      await localforage.setItem("coord_delegados_activos_cache", realesDelegados)
      await localforage.setItem("coord_recinto_reasignacion", recintoAsignado)
      await localforage.setItem("coord_global_reasignacion", isGlobalBackend)
    } catch (error) {
      console.log("Cargando logística desde caché offline...", error)
      if (error?.response?.status === 403) {
        setErrorAcceso(error.response.data.message || "Usuario no autorizado.")
      } else {
        const jCache = (await localforage.getItem("coord_juntas_cache")) || []
        const dCache = (await localforage.getItem("coord_delegados_activos_cache")) || []
        const rCache = (await localforage.getItem("coord_recinto_reasignacion")) || ""
        const gCache = (await localforage.getItem("coord_global_reasignacion")) || false

        setJuntas(jCache)
        setDelegadosActivos(dCache)
        setRecintoActual(rCache)
        setEsGlobal(gCache)
      }
    } finally {
      setIsLoading(false)
    }
  }

  // ========================================================
  // 2. LÓGICA DE REASIGNACIÓN
  // ========================================================
  const handleCambioSelect = (junta_codigo, nuevo_del_codigo) => {
    setCambiosPendientes((prev) => ({
      ...prev,
      [junta_codigo]: nuevo_del_codigo,
    }))
  }

  const confirmarReasignacion = async (junta_codigo) => {
    const nuevoDelegadoId = cambiosPendientes[junta_codigo]
    if (!nuevoDelegadoId) return

    const delegadoData = delegadosActivos.find((d) => d.del_codigo === nuevoDelegadoId)

    const juntasActualizadas = juntas.map((j) => {
      if (j.junta_codigo === junta_codigo) {
        return { ...j, del_codigo: delegadoData.del_codigo, del_nombre: delegadoData.nombre, del_asistencia: 1 }
      }
      return j
    })
    setJuntas(juntasActualizadas)
    await localforage.setItem("coord_juntas_cache", juntasActualizadas)

    const nuevosCambios = { ...cambiosPendientes }
    delete nuevosCambios[junta_codigo]
    setCambiosPendientes(nuevosCambios)

    const payload = {
      junta_codigo,
      nuevo_del_codigo: nuevoDelegadoId,
      timestamp: new Date().toISOString(),
    }

    if (isOffline) {
      const cola = (await localforage.getItem("reasignacion_offline_queue")) || []
      cola.push(payload)
      await localforage.setItem("reasignacion_offline_queue", cola)
      setColaReasignacion(cola)

      Swal.fire({
        toast: true,
        position: "bottom-end",
        icon: "info",
        title: "Guardado Offline",
        showConfirmButton: false,
        timer: 2000,
      })
    } else {
      try {
        await api.post("/ControlElectoral/reasignarMesa", payload)
        Swal.fire({
          toast: true,
          position: "bottom-end",
          icon: "success",
          title: "Reasignación Exitosa",
          showConfirmButton: false,
          timer: 2000,
        })
      } catch (error) {
        const cola = (await localforage.getItem("reasignacion_offline_queue")) || []
        cola.push(payload)
        await localforage.setItem("reasignacion_offline_queue", cola)
        setColaReasignacion(cola)
      }
    }
  }

  // ========================================================
  // 3. SINCRONIZACIÓN MASIVA
  // ========================================================
  const sincronizarCola = async () => {
    if (isOffline) return Swal.fire("Sin señal", "Acérquese a una ventana para sincronizar.", "warning")
    const cola = (await localforage.getItem("reasignacion_offline_queue")) || []
    if (cola.length === 0) return

    setIsSyncing(true)
    let colaRestante = [...cola]

    for (let i = 0; i < cola.length; i++) {
      try {
        await api.post("/ControlElectoral/reasignarMesa", cola[i])
        colaRestante = colaRestante.filter((_, index) => index !== i)
      } catch (err) {
        console.error("Fallo al subir reasignación", err)
      }
    }

    await localforage.setItem("reasignacion_offline_queue", colaRestante)
    setColaReasignacion(colaRestante)
    setIsSyncing(false)

    if (colaRestante.length === 0) {
      Swal.fire("Sincronizado", "Todas las reasignaciones están en la base central.", "success")
    }
  }

  const juntasOrdenadas = [...juntas].sort((a, b) => a.del_asistencia - b.del_asistencia)

  return (
    <ThemeProvider theme={theme}>
      <Header />

      <div className="main main-app p-2" style={{ backgroundColor: "#f0f2f5", minHeight: "100vh" }}>
        <BackIcon />

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          p={2}
          bgcolor="white"
          borderRadius={2}
          boxShadow={1}
        >
          <Box display="flex" alignItems="center">
            {isOffline ? <CloudOff color="error" sx={{ mr: 1 }} /> : <CloudDone color="success" sx={{ mr: 1 }} />}
            <Typography variant="body2" fontWeight="bold" color={isOffline ? "error" : "success.main"}>
              {isOffline ? "MODO OFFLINE ACTIVADO" : "CONEXIÓN ESTABLE"}
            </Typography>
          </Box>
          <Badge badgeContent={colaReasignacion.length} color="secondary">
            <IconButton
              onClick={sincronizarCola}
              disabled={colaReasignacion.length === 0 || isOffline}
              sx={{ bgcolor: "#fff3e0" }}
            >
              <Sync color={colaReasignacion.length > 0 ? "secondary" : "disabled"} />
            </IconButton>
          </Badge>
        </Box>

        <Typography variant="h5" color="secondary.main" fontWeight="bold" mb={1} display="flex" alignItems="center">
          <SwapHoriz sx={{ mr: 1, fontSize: 32 }} /> Reasignación de Mesas
        </Typography>
        <Typography variant="body2" color="textSecondary" mb={2}>
          Transfiera las mesas de los delegados ausentes al personal que sí se encuentra disponible.
        </Typography>

        {errorAcceso ? (
          <Alert severity="error" variant="filled" sx={{ mt: 4, borderRadius: 2, p: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              ACCESO RESTRINGIDO
            </Typography>
            <Typography variant="body1">{errorAcceso}</Typography>
          </Alert>
        ) : (
          <>
            {recintoActual && (
              <Box display="flex" alignItems="center" mb={3}>
                <Typography variant="subtitle1" color="primary" fontWeight="bold" display="flex" alignItems="center">
                  📍 Cobertura: {recintoActual}
                </Typography>
                {esGlobal && (
                  <Chip
                    icon={<Public fontSize="small" />}
                    label="MODO GLOBAL"
                    color="info"
                    size="small"
                    sx={{ ml: 2, fontWeight: "bold" }}
                  />
                )}
              </Box>
            )}

            <Grid container spacing={2} pb={4}>
              {juntasOrdenadas.map((junta) => {
                const isPeligro = junta.del_asistencia === 0
                const tieneCambioPendiente = !!cambiosPendientes[junta.junta_codigo]

                return (
                  <Grid item xs={12} md={6} key={junta.junta_codigo}>
                    <Card
                      elevation={2}
                      sx={{ borderRadius: 3, borderLeft: isPeligro ? "6px solid #d32f2f" : "6px solid #196C87" }}
                    >
                      <CardContent sx={{ p: "16px !important" }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                          <Box>
                            <Typography
                              variant="h6"
                              fontWeight="bold"
                              color={isPeligro ? "error.main" : "textPrimary"}
                              lineHeight={1.1}
                            >
                              {junta.nombre}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Cód: {junta.junta_codigo} {esGlobal && ` | Recinto: ${junta.reci_codigo}`}
                            </Typography>
                          </Box>
                          {isPeligro ? (
                            <Chip
                              size="small"
                              icon={<Warning />}
                              label="MESA EN RIESGO"
                              color="error"
                              sx={{ fontWeight: "bold" }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="Cubierta"
                              color="success"
                              variant="outlined"
                              sx={{ fontWeight: "bold" }}
                            />
                          )}
                        </Box>

                        <Box
                          display="flex"
                          alignItems="center"
                          mb={2}
                          p={1.5}
                          bgcolor={isPeligro ? "#ffebee" : "#f5f5f5"}
                          borderRadius={2}
                        >
                          {isPeligro ? (
                            <PersonOff color="error" sx={{ mr: 1 }} />
                          ) : (
                            <Avatar sx={{ width: 24, height: 24, fontSize: 12, mr: 1, bgcolor: "#196C87" }}>
                              {junta.del_nombre.charAt(0)}
                            </Avatar>
                          )}
                          <Box>
                            <Typography variant="caption" display="block" color="textSecondary" lineHeight={1}>
                              Delegado Asignado:
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color={isPeligro ? "error.main" : "textPrimary"}
                            >
                              {junta.del_nombre} {isPeligro && "(AUSENTE/SIN ASIGNAR)"}
                            </Typography>
                          </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="caption" fontWeight="bold" color="secondary.main" display="block" mb={1}>
                          TRANSFERIR MESA A:
                        </Typography>

                        <Box display="flex" gap={1}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Seleccione delegado presente"
                            value={cambiosPendientes[junta.junta_codigo] || ""}
                            onChange={(e) => handleCambioSelect(junta.junta_codigo, e.target.value)}
                            disabled={delegadosActivos.length === 0}
                          >
                            {delegadosActivos.map((del) => (
                              <MenuItem key={del.del_codigo} value={del.del_codigo}>
                                {del.nombre} {esGlobal ? `(Recinto: ${del.reci_codigo})` : ""}
                              </MenuItem>
                            ))}
                          </TextField>

                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => confirmarReasignacion(junta.junta_codigo)}
                            disabled={!tieneCambioPendiente}
                            sx={{ minWidth: "120px", fontWeight: "bold" }}
                            startIcon={<Save />}
                          >
                            GUARDAR
                          </Button>
                        </Box>

                        {delegadosActivos.length === 0 && (
                          <Typography variant="caption" color="error" mt={1} display="block">
                            No hay delegados presentes para reasignar.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                )
              })}
            </Grid>
          </>
        )}
      </div>
    </ThemeProvider>
  )
}

export default ReasignacionMesas
